import { PageContainer, ProFormSelect, ProFormText, QueryFilter } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Empty, Flex, Pagination, Row, Statistic, Typography } from 'antd';
import { useMemo } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { buildStore360Path } from '@/common/utils/store360-navigation';
import { useQueryParams } from '@/common/hooks/use-query-params';
import StoreDirectoryCard from '../components/StoreDirectoryCard';
import { queryStoreDirectory, queryStoreDirectorySummary } from '../services/stores';
import {
  formatStoreAmountWan,
  formatStoreOperatingRatio,
  formatStoreTargetDescription,
} from '../utils/store-directory-summary';

const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [12, 24, 48];

const getQueryText = (value) => (typeof value === 'string' ? value.trim() : '');
const getPositiveInteger = (value, fallback) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : fallback;
};

const StoreList = () => {
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const [query, setQuery] = useQueryParams();
  const name = getQueryText(query.name);
  const regionId = getQueryText(query.regionId);
  const businessFormat = getQueryText(query.businessFormat);
  const businessStatus = getQueryText(query.businessStatus);
  const current = getPositiveInteger(query.page, 1);
  const requestedPageSize = getPositiveInteger(query.pageSize, DEFAULT_PAGE_SIZE);
  const pageSize = PAGE_SIZE_OPTIONS.includes(requestedPageSize)
    ? requestedPageSize
    : DEFAULT_PAGE_SIZE;
  const formValues = useMemo(
    () => ({
      businessFormat: businessFormat || undefined,
      businessStatus: businessStatus || undefined,
      name: name || undefined,
      regionId: regionId || undefined,
    }),
    [businessFormat, businessStatus, name, regionId],
  );
  const formKey = JSON.stringify([name, regionId, businessFormat, businessStatus]);
  const { data = {}, isPending } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () =>
      queryStoreDirectory({
        businessFormat: businessFormat || undefined,
        businessStatus: businessStatus || undefined,
        current,
        name: name || undefined,
        operationEntityParentId: regionId || undefined,
        organizationId,
        pageSize,
      }),
    queryKey: [
      'store-directory',
      organizationId,
      name,
      regionId,
      businessFormat,
      businessStatus,
      current,
      pageSize,
    ],
  });
  const { data: summaryResponse = {}, isPending: isSummaryPending } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryStoreDirectorySummary({ organizationId }),
    queryKey: ['store-directory-summary', organizationId],
  });
  const stores = data.data || [];
  const meta = data.meta || {};
  const summary = summaryResponse.data || {};
  const storeIncomeAmountWan = formatStoreAmountWan(summary.storeIncomeAmount);
  const stallIncomeAmountWan = formatStoreAmountWan(summary.stallIncomeAmount);

  return (
    <PageContainer title={false} loading={isPending}>
      <Flex vertical gap="large">
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={6}>
            <Card data-testid="store-summary-total" variant="outlined" loading={isSummaryPending}>
              <Statistic title="门店总数" value={summary.totalStoreCount ?? 0} />
              <Typography.Text type="secondary">包含营业、筹备、闭店的门店</Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              data-testid="store-summary-operating"
              variant="outlined"
              loading={isSummaryPending}
            >
              <Statistic title="营业门店" value={summary.operatingStoreCount ?? 0} />
              <Typography.Text type="secondary">
                占比
                {formatStoreOperatingRatio(summary.operatingStoreCount, summary.totalStoreCount)}
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card data-testid="store-summary-income" variant="outlined" loading={isSummaryPending}>
              <Statistic
                precision={storeIncomeAmountWan === null ? undefined : 2}
                suffix={storeIncomeAmountWan === null ? undefined : '万'}
                title="本月门店总收入（含店外档口）"
                value={storeIncomeAmountWan ?? '-'}
              />
              <Typography.Text type="secondary">
                {formatStoreTargetDescription(summary.storeIncomeTargetAmount)}
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              data-testid="store-summary-stall-income"
              variant="outlined"
              loading={isSummaryPending}
            >
              <Statistic
                precision={stallIncomeAmountWan === null ? undefined : 2}
                suffix={stallIncomeAmountWan === null ? undefined : '万'}
                title="本月店外档口总收入"
                value={stallIncomeAmountWan ?? '-'}
              />
              <Typography.Text type="secondary">
                {formatStoreTargetDescription(summary.stallIncomeTargetAmount)}
              </Typography.Text>
            </Card>
          </Col>
        </Row>
        <Card
          data-testid="store-list-filter-card"
          variant="borderless"
          styles={{ body: { padding: 0 } }}
        >
          <QueryFilter
            key={formKey}
            defaultCollapsed
            initialValues={formValues}
            labelWidth={80}
            onFinish={async (values) => {
              setQuery({
                businessFormat: values.businessFormat,
                businessStatus: values.businessStatus,
                name: getQueryText(values.name) || undefined,
                page: undefined,
                pageSize: undefined,
                regionId: values.regionId,
              });
              return true;
            }}
            onReset={() => setQuery({}, { reset: true })}
          >
            <ProFormText name="name" label="名称" />
            <ProFormSelect
              name="regionId"
              label="区域"
              options={meta.regionOptions || []}
              fieldProps={{ allowClear: true, optionFilterProp: 'label', showSearch: true }}
            />
            <ProFormSelect
              name="businessFormat"
              label="业态"
              options={meta.businessFormatOptions || []}
              fieldProps={{ allowClear: true, optionFilterProp: 'label', showSearch: true }}
            />
            <ProFormSelect
              name="businessStatus"
              label="状态"
              options={meta.businessStatusOptions || []}
              fieldProps={{ allowClear: true, optionFilterProp: 'label', showSearch: true }}
            />
          </QueryFilter>
        </Card>
        <Flex vertical gap="middle">
          {stores.length > 0 ? (
            <Row gutter={[24, 24]}>
              {stores.map((store) => (
                <Col key={store.id} xs={24} md={12} xl={8}>
                  <StoreDirectoryCard
                    store={store}
                    to={resolveOrganizationPath(buildStore360Path(store.operationEntityId))}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              description={
                name || regionId || businessFormat || businessStatus
                  ? '未找到符合条件的门店'
                  : '暂无可访问门店'
              }
            />
          )}
          <Flex justify="flex-end">
            <Pagination
              current={meta.page || current}
              pageSize={meta.pageSize || pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showSizeChanger
              showTotal={(total) => `共 ${total} 家门店`}
              total={meta.total || 0}
              onChange={(nextPage, nextPageSize) => {
                setQuery({
                  page: nextPage === 1 ? undefined : nextPage,
                  pageSize: nextPageSize === DEFAULT_PAGE_SIZE ? undefined : nextPageSize,
                });
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </PageContainer>
  );
};

export default StoreList;
